import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Reveal, RevealGroup, riseItem } from '../motion/Reveal';
import payments from '../assets/figma/images/feature-payments.webp';
import secure from '../assets/figma/images/feature-secure.webp';
import cards from '../assets/figma/images/feature-cards.webp';
import crypto from '../assets/figma/images/feature-crypto.webp';

/** Figma 59:9942 — heading over a 2×2 grid of cards.
 *
 *  Each card's upper half is an app mock-up: a rendered scene, not content, so
 *  it comes straight out of Figma as an exported image at 3× rather than being
 *  rebuilt in markup. The heading and body underneath are real copy.
 *
 *  The four scenes drift on their own slow cycles — deliberately out of phase,
 *  so the grid breathes instead of pulsing in unison — and lean toward the
 *  cursor on hover. */
const CARDS = [
  { key: 'payments', image: payments, drift: 8, period: 6 },
  { key: 'secure', image: secure, drift: -10, period: 7.5 },
  { key: 'cards', image: cards, drift: 10, period: 8 },
  { key: 'crypto', image: crypto, drift: -8, period: 6.8 },
] as const;

export function Features() {
  const { t } = useTranslation();
  const still = useReducedMotion();

  return (
    <section id="features" className="mx-auto w-full px-[16px] lg:w-[1208px] lg:px-0">
      <Reveal>
        <h2 className="text-center font-display text-[32px] font-medium leading-[1.05] text-ink sm:text-[44px] lg:text-[72px] lg:leading-[0.9]">
          {t('features.title')}
        </h2>
      </Reveal>

      <RevealGroup className="mt-[32px] grid grid-cols-1 gap-[24px] lg:mt-[60px] lg:grid-cols-2 lg:gap-x-[24px] lg:gap-y-[32px]">
        {CARDS.map(({ key, image, drift, period }) => (
          <motion.article key={key} variants={riseItem} className="flex flex-col gap-[24px]">
            <motion.div
              className="aspect-[592/440] w-full overflow-hidden rounded-[32px] lg:h-[440px] lg:w-[592px] lg:rounded-[54px]"
              whileHover={still ? undefined : { scale: 1.015 }}
              transition={{ type: 'spring', stiffness: 220, damping: 24 }}
            >
              <img
                src={image}
                alt=""
                className="aw-drift h-full w-full object-cover lg:h-[440px] lg:w-[592px]"
                style={
                  {
                    '--aw-drift': `${drift}px`,
                    '--aw-duration': `${period}s`,
                  } as React.CSSProperties
                }
              />
            </motion.div>
            <div className="flex flex-col gap-[10px] px-[8px] lg:gap-[16px] lg:px-[32px]">
              <h3 className="text-[20px] font-medium leading-[1.2] text-ink lg:text-[28px]">
                {t(`features.${key}.title`)}
              </h3>
              <p className="text-[15px] font-normal leading-[1.35] text-[rgba(11,18,25,0.55)] lg:text-[20px] lg:leading-[1.2]">
                {t(`features.${key}.body`)}
              </p>
            </div>
          </motion.article>
        ))}
      </RevealGroup>
    </section>
  );
}
